import React, {useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Button, Card, Col, Descriptions, Grid, Row} from 'antd';
import {Shell} from '../../../components/Shell';
import {useNavigate, useParams} from 'react-router-dom';
import {AppRoutePaths} from '../../../routing/Routes';
import {CustomerState} from '../../../store/users/customers/CustomerState';
import {useTranslation} from 'react-i18next';
import {CardTitle, DescriptionLabel} from 'views/serviceActions/utils/ServicesUtils';
import {getIsMobile} from 'helpers/utils';

export const CustomerDisplayView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {customerId} = useParams();
  const [state] = useState(() => new CustomerState());

  const screen = Grid.useBreakpoint();
  const isMobile = useMemo(() => getIsMobile(screen), [screen]);

  useEffect(() => state.loadCustomer(Number(customerId)), [customerId, state]);

  return (
    <Shell
      backLink
      title={
        <>
          {state.item.lastName} {state.item.firstName}
        </>
      }
      actionButtons={[
        <Button
          key="edit"
          size="large"
          className="shell-btn"
          onClick={() => navigate(`${AppRoutePaths.customers.url}/${customerId}/edit`)}
        >
          {t('Edit customer')}
        </Button>,
      ]}
    >
      <Row gutter={16}>
        <Col className="flex flex-col gap-4" span={isMobile ? 24 : 12}>
          <Card title={<CardTitle text={t('Customer data')} />}>
            <Descriptions column={2} colon={false} layout="vertical">
              <Descriptions.Item label={<DescriptionLabel text={t('Email')} />}>{state.item.email}</Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Additional email')} />}>
                {state.item.additionalEmail}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Phone')} />}>{state.item.phone}</Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Additional phone')} />}>
                {state.item.additionalPhone}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Name')} />}>
                {state.item.lastName}, {state.item.firstName}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col className="flex flex-col gap-4 mt-4 md:mt-0" span={isMobile ? 24 : 12}>
          <Card title={<CardTitle text={t('Company data')} />} className="h-full">
            <Descriptions column={2} colon={false} layout="vertical">
              <Descriptions.Item label={<DescriptionLabel text={t('Company name')} />}>
                {state.item.companyName}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('NIP')} />}>{state.item.nip}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
      <Row gutter={16} className="mt-4">
        <Col className="flex flex-col gap-4" span={isMobile ? 24 : 12}>
          <Card title={<CardTitle text={t('Billing Address')} />} className="relative">
            <Descriptions column={2} colon={false} layout="vertical">
              <Descriptions.Item label={<DescriptionLabel text={t('City')} />}>
                {state.item.billAddress?.city}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Street with local')} />}>
                {state.item.billAddress?.streetWithLocal}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Street')} />}>
                {state.item.billAddress?.street}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Building number')} />}>
                {state.item.billAddress?.buildingNumber}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Appartment number')} />}>
                {state.item.billAddress?.apartmentNumber}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Postal code')} />}>
                {state.item.billAddress?.postalCode}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col className="flex flex-col gap-4 mt-4 md:mt-0" span={isMobile ? 24 : 12}>
          <Card title={<CardTitle text={t('Correspondence Address')} />} className="relative">
            <Descriptions column={2} colon={false} layout="vertical">
              <Descriptions.Item label={<DescriptionLabel text={t('City')} />}>
                {state.item.correspondenceAddress?.city}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Street with local')} />}>
                {state.item.correspondenceAddress?.streetWithLocal}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Street')} />}>
                {state.item.correspondenceAddress?.street}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Building number')} />}>
                {state.item.correspondenceAddress?.buildingNumber}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Appartment number')} />}>
                {state.item.correspondenceAddress?.apartmentNumber}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Postal code')} />}>
                {state.item.correspondenceAddress?.postalCode}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </Shell>
  );
});
